import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import Highlighter from "react-highlight-words";
import { Controller, FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { ArrowForwardIos, Edit, ErrorOutline, TextFormat } from "@mui/icons-material";
import {
  Box,
  Button,
  inputClasses,
  OutlinedInput,
  outlinedInputClasses,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography as T,
  Select,
  MenuItem,
  selectClasses,
  SelectChangeEvent,
  Tooltip,
  IconButton,
} from "@mui/material";
import MarkdownModal from "datasets/components/MarkdownModal";
import IngestFormErrors from "datasets/pages/tables/components/IngestFormErrors";
import MarkdownCollapse from "datasets/pages/tables/components/MarkdownCollapse";
// TODO: Re-enable when tags are supported during ingest
// import TagsFormControl from "datasets/components/TagsFormControl";
import useMetadataContext from "datasets/pages/tables/contexts/metadata";
import useColumnDatatypes from "datasets/pages/tables/hooks/useColumnDatatypes";
import useFileMetadata from "datasets/pages/tables/hooks/useFileMetadata";
import useGeneratedMetadata from "datasets/pages/tables/hooks/useGeneratedMetadata";
import { MetadataMode, MetadataStatus } from "datasets/pages/tables/types/enums/metadata";
import { GeneratedMetadata, Ingest } from "datasets/pages/tables/types/interfaces/ingest";
import { RESERVED_KEYWORDS } from "datasets/pages/tables/utils/reserved-keywords";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { SearchIcon } from "~/assets/icons";
import useErrorToast from "~/contexts/error-toast";
import rumClient from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const StyledTHeaderCell = styled(TableCell)({
  fontWeight: 400,
  lineHeight: "20px",
  opacity: 0.6,
  padding: "8px 8px",
  "&:first-of-type": {
    paddingLeft: 16,
  },
  "&:last-of-type": {
    paddingRight: 16,
  },
});

const StyledTBodyCell = styled(TableCell)({
  padding: "20px 8px",
  position: "relative",
  "&:first-of-type": {
    paddingLeft: 16,
  },
  "&:last-of-type": {
    paddingRight: 16,
  },
});

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  gap: 4,
  padding: 8,
  [`.${inputClasses.inputSizeSmall}`]: {
    height: "unset",
    lineHeight: "20px",
    padding: 0,
  },
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    borderColor: theme.palette.grey[300],
  },
}));

const TableMetadataRow = ({
  autoExpand,
  column,
  disabled = false,
  highlightText,
  index,
}: {
  autoExpand: boolean | null;
  column: FieldArrayWithId<Ingest, "metadata_config.columns", "id">;
  disabled: boolean;
  highlightText: string;
  index: number;
}) => {
  const { data: ingestTypes, isLoading: loadingIngestTypes } = useColumnDatatypes();
  const { control, register, setValue, trigger, watch } = useFormContext<Ingest>();
  const formColumn = watch(`metadata_config.columns.${index}`);

  const [columnDescriptionModalOpen, setColumnDescriptionModalOpen] = useState(false);
  const [showDescriptionEditButton, setShowDescriptionEditButton] = useState(false);

  const validDatatype = ingestTypes && ingestTypes.includes(column.type);

  const updateDescription = (description: string) => {
    setValue(`metadata_config.columns.${index}.description`, description);
    setColumnDescriptionModalOpen(false);
  };

  // Helps trigger validation for read-only 'name' field
  useEffect(() => {
    if (formColumn) {
      trigger(`metadata_config.columns.${index}`);
    }
  }, [formColumn, index, trigger]);

  return (
    <TableRow
      key={column.id}
      sx={{
        verticalAlign: "top",
        td: {
          overflowWrap: "break-word",
          wordBreak: "normal",
        },
        "&:last-of-type td": { borderBottom: "unset" },
      }}
    >
      <StyledTBodyCell>
        <Controller
          control={control}
          name={`metadata_config.columns.${index}.name`}
          rules={{
            required: true,
            validate: {
              correctStart: (value: string) =>
                /^[A-Za-z_]/.test(value) ? true : 'Must start with a letter or underscore "_".',
              validChars: (value: string) =>
                /^[A-Za-z0-9_$]*$/.test(value)
                  ? true
                  : 'Can only contain letters, numbers, "_" or "$".',
              notReserved: (value: string) =>
                !RESERVED_KEYWORDS.includes(value)
                  ? true
                  : `"${value}" is a reserved keyword and cannot be used.`,
            },
          }}
          render={({ field, fieldState: { error } }) => {
            const showUppercaseWarning = field.value !== field.value.toUpperCase();
            let tooltipMessage = "Column names are automatically converted to uppercase.";

            if (error && error.message) {
              tooltipMessage = error.message + " " + tooltipMessage;
            }

            return (
              <Stack gap={0.5}>
                <Box
                  sx={{
                    alignItems: "center",
                    color: error ? "attention.main" : undefined,
                    display: "flex",
                    gap: 0.5,
                    justifyContent: "space-between",
                    minHeight: 36,
                  }}
                >
                  <Highlighter
                    autoEscape
                    highlightClassName="highlight"
                    searchWords={[highlightText]}
                    textToHighlight={field.value.toUpperCase()}
                  />
                  {(showUppercaseWarning || error) && (
                    <Tooltip arrow title={tooltipMessage} enterDelay={500}>
                      {error ? (
                        <ErrorOutline sx={{ fontSize: 18 }} />
                      ) : (
                        <TextFormat sx={{ fontSize: 18, opacity: 0.8 }} />
                      )}
                    </Tooltip>
                  )}
                </Box>
              </Stack>
            );
          }}
        />
      </StyledTBodyCell>
      <StyledTBodyCell>
        <Select
          {...register(`metadata_config.columns.${index}.type`, {
            required: true,
          })}
          fullWidth
          disabled={disabled}
          size="small"
          defaultValue={validDatatype ? column.type : ingestTypes ? ingestTypes[0] : undefined}
          IconComponent={ArrowForwardIos}
          sx={{
            lineHeight: "20px",
            [`.${selectClasses.icon}`]: { transform: "rotate(90deg)", width: 16 },
            [`.${selectClasses.iconOpen}`]: { transform: "rotate(-90deg)" },
            [`.${selectClasses.outlined}`]: { p: 1 },
            [`.${outlinedInputClasses.notchedOutline}`]: { borderColor: "grey.300" },
          }}
        >
          {!loadingIngestTypes &&
            ingestTypes!.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
        </Select>
      </StyledTBodyCell>
      <StyledTBodyCell
        sx={{ width: "100%" }}
        onMouseEnter={() => {
          if (!disabled) {
            setShowDescriptionEditButton(true);
          }
        }}
        onMouseLeave={() => setShowDescriptionEditButton(false)}
      >
        <Controller
          control={control}
          name={`metadata_config.columns.${index}.description`}
          render={({ field }) => (
            <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
              <MarkdownCollapse autoExpand={autoExpand} editable value={field.value} maxLines={3} />
              {showDescriptionEditButton && (
                <>
                  <Tooltip arrow title="Edit description" enterDelay={500}>
                    <IconButton
                      sx={{ width: 36, height: 36, position: "absolute", right: 16, top: 20 }}
                      onClick={() => setColumnDescriptionModalOpen(true)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {columnDescriptionModalOpen && (
                    <MarkdownModal
                      onClose={() => setColumnDescriptionModalOpen(false)}
                      onSubmit={updateDescription}
                      open={columnDescriptionModalOpen}
                      title={`Edit description for ${column.name}`}
                      value={field.value ?? ""}
                    />
                  )}
                </>
              )}
            </Box>
          )}
        />
      </StyledTBodyCell>
    </TableRow>
  );
};

const TableMetadataDetails = ({ onNextPanel }: { onNextPanel: () => void }) => {
  const { showError } = useErrorToast();
  const { mode, setMode } = useMetadataContext();
  const { control, setValue, watch, formState } = useFormContext<Ingest>();
  const sourceFile = watch("platform_storage_key");
  const sourceMetadataFile = watch("metadata_platform_storage_key");
  const columns = watch("metadata_config.columns");

  const { isLoading: loadingDatatypes } = useColumnDatatypes();
  const { data: fileMetadata, isLoading: loadingFile } = useFileMetadata(sourceMetadataFile);
  const {
    data: llmMetadata,
    error: llmError,
    isLoading: loadingLlm,
    mutate: mutateLlm,
  } = useGeneratedMetadata(sourceFile);

  const { fields } = useFieldArray({
    control,
    name: "metadata_config.columns",
  });

  const [columnSearch, setColumnSearch] = useQueryParam("search", withDefault(StringParam, ""));
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);

  // Reset generation attempt flag when source file changes
  useEffect(() => {
    setHasAttemptedGeneration(false);
  }, [sourceFile]);

  const highlightText = columnSearch || "";

  const filteredColumns = useMemo(
    () =>
      fields.filter((column) => {
        return (
          column.name.toLowerCase().includes(columnSearch.toLowerCase()) ||
          column.description?.toLowerCase().includes(columnSearch.toLowerCase())
        );
      }),
    [columnSearch, fields]
  );

  const generateMetadata = useCallback(async () => {
    setIsGenerating(true);
    setHasAttemptedGeneration(true);

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/generate-metadata`, {
        method: "POST",
        body: JSON.stringify({
          platform_storage_key: sourceFile,
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`Failed to generate metadata: HTTP ${resp.status} - ${errorText}`);
      }

      const respData: GeneratedMetadata = await resp.json();
      await mutateLlm();
      setValue("metadata_config.columns", respData.columns);
      setMode(MetadataMode.GENERATED);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      showError(e.message);
      setIsGenerating(false);
    }
  }, [mutateLlm, setMode, setValue, showError, sourceFile]);

  const handleChangeMode = (event: SelectChangeEvent) => {
    const newMode = event.target.value;
    setMode(newMode as MetadataMode);

    if (newMode === MetadataMode.FILE && fileMetadata) {
      setValue("metadata_config.columns", fileMetadata.columns);
    } else if (newMode === MetadataMode.GENERATED && llmMetadata) {
      setValue("metadata_config.columns", llmMetadata.columns);
    }
  };

  // Handles initial mode and columns to use based on existing metadata
  useEffect(() => {
    // Wait for loading to finish. Exit early if metadata mode is already set.
    if (mode || loadingFile || loadingLlm || !sourceFile) {
      return;
    }

    if (fileMetadata) {
      // If fileMetadata exists, treat as primary mode
      setMode(MetadataMode.FILE);
    } else if (llmMetadata) {
      // If generated metadata exists without fileMetadata, set mode to generated and update form
      setMode(MetadataMode.GENERATED);
    } else if (!hasAttemptedGeneration && (!llmError || llmError.status === 404)) {
      // Only auto-generate if we haven't tried before and there's no error (or if it's just a 404, which is expected)
      generateMetadata();
    }
  }, [
    mode,
    loadingFile,
    loadingLlm,
    fileMetadata,
    llmMetadata,
    llmError,
    sourceFile,
    setMode,
    hasAttemptedGeneration,
    generateMetadata,
  ]);

  // Handle column metadata as it is generated
  useEffect(() => {
    if (mode === MetadataMode.GENERATED && llmMetadata && llmMetadata.columns) {
      setValue("metadata_config.columns", llmMetadata.columns);
    }
  }, [llmMetadata, mode, setValue]);

  // Handle completion or failure of metadata generation to update loading state.
  useEffect(() => {
    if (
      llmMetadata &&
      llmMetadata.columns &&
      (llmMetadata.status === MetadataStatus.COMPLETED ||
        llmMetadata.status === MetadataStatus.FAILED)
    ) {
      setIsGenerating(false);
    }
  }, [llmMetadata]);

  // Handle LLM errors with better user feedback
  useEffect(() => {
    if (llmError && hasAttemptedGeneration) {
      if (llmError.status === 404) {
        // 404 is expected for files without metadata - don't show error
        console.log("No existing metadata found for file");
      } else if (llmError.status === 409) {
        showError("Metadata is stale and needs to be regenerated");
      } else {
        // Only show generation errors, not fetch errors for existing metadata
        if (isGenerating) {
          showError(`Failed to generate metadata: ${llmError.message || "Unknown error"}`);
        }
      }
    }
  }, [llmError, hasAttemptedGeneration, showError, isGenerating]);

  // Check if we have valid columns for ingestion
  const hasValidColumns = columns && columns.length > 0;
  const isFormValidForIngestion = formState.isValid && hasValidColumns;

  return (
    <>
      <Stack gap={0.5} width="100%">
        <Controller
          control={control}
          name="metadata_config.table_description"
          render={({ field }) => (
            <>
              <T variant="body2" fontWeight={500}>
                Description
              </T>
              <StyledOutlinedInput
                {...field}
                fullWidth
                multiline
                minRows={2}
                placeholder="Describe this table..."
              />
            </>
          )}
        />
      </Stack>

      <Stack gap={1.25} width="100%">
        <Box display="flex" alignItems="end" width="100%" gap={1}>
          <Stack gap={0.5} flex={1}>
            <T variant="body2" fontWeight={500}>
              Columns
            </T>
            <StyledOutlinedInput
              size="small"
              placeholder="Search by column name..."
              startAdornment={
                <Box display="flex" alignItems="center" justifyContent="center">
                  <SearchIcon width={16} />
                </Box>
              }
              value={columnSearch}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = event.currentTarget.value;
                setColumnSearch(value, "replaceIn");
              }}
            />
          </Stack>

          <Stack gap={0.5} minWidth={200}>
            <T variant="body2" fontWeight={500}>
              Metadata Source
            </T>
            <Select
              size="small"
              disabled={isGenerating || (!fileMetadata && !llmMetadata)}
              value={mode ?? ""}
              IconComponent={ArrowForwardIos}
              onChange={handleChangeMode}
              sx={{
                lineHeight: "20px",
                textTransform: "capitalize",
                [`.${selectClasses.icon}`]: { transform: "rotate(90deg)", width: 16 },
                [`.${selectClasses.iconOpen}`]: { transform: "rotate(-90deg)" },
                [`.${selectClasses.outlined}`]: { p: 1 },
                [`.${outlinedInputClasses.notchedOutline}`]: { borderColor: "grey.300" },
              }}
            >
              <MenuItem value={MetadataMode.FILE} disabled={!fileMetadata}>
                File
              </MenuItem>
              <MenuItem value={MetadataMode.GENERATED} disabled={!llmMetadata}>
                Generated
              </MenuItem>
            </Select>
          </Stack>

          <Button
            color="secondary"
            variant="contained"
            loading={isGenerating}
            loadingPosition="end"
            onClick={generateMetadata}
            sx={{ bgcolor: "grey.100" }}
          >
            Generate with AI
          </Button>
        </Box>
        <IngestFormErrors loading={isGenerating} />
        <TableContainer
          sx={{
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "grey.300",
            borderRadius: 1.5,
          }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "common.dimmedPageBackground" }}>
              <TableRow>
                <StyledTHeaderCell sx={{ minWidth: 300 }}>Name</StyledTHeaderCell>
                <StyledTHeaderCell sx={{ minWidth: 220 }}>Type</StyledTHeaderCell>
                <StyledTHeaderCell sx={{ minWidth: 360 }}>Description</StyledTHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredColumns.map((column, index) => (
                <TableMetadataRow
                  key={column.id}
                  autoExpand={columnSearch === column.name ? true : null}
                  column={column}
                  disabled={isGenerating || loadingDatatypes}
                  highlightText={highlightText}
                  index={index}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Button
        variant="contained"
        disabled={isGenerating || !isFormValidForIngestion}
        onClick={() => onNextPanel()}
      >
        Next
      </Button>
    </>
  );
};

export default TableMetadataDetails;
